import { useEffect, useState } from "react";
import axios from "axios";
import { History, Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/lib/api";

const FIELDS = [
  { key: "footer_tagline", label: "Footer Tagline" },
  { key: "footer_description", label: "Footer Description" },
  { key: "vision_heading", label: "Vision — Heading" },
  { key: "vision_text", label: "Vision — Statement" },
  { key: "value_integrity", label: "Value — Integrity" },
  { key: "value_innovation", label: "Value — Innovation" },
  { key: "value_customer_first", label: "Value — Customer First" },
  { key: "value_excellence", label: "Value — Excellence" },
  { key: "hero_line_1", label: "Homepage Headline — Line 1" },
  { key: "hero_line_2", label: "Homepage Headline — Line 2" },
  { key: "hero_line_3", label: "Homepage Headline — Line 3" },
  { key: "hero_line_4", label: "Homepage Headline — Line 4" },
  { key: "hero_subtext", label: "Homepage Headline — Supporting Text" },
  { key: "hero_btn_primary", label: "Homepage Button — Primary" },
  { key: "hero_btn_secondary", label: "Homepage Button — Secondary" },
  { key: "about_story_1", label: "About Page — Story Paragraph 1" },
  { key: "about_story_2", label: "About Page — Story Paragraph 2" },
  { key: "about_story_3", label: "About Page — Story Paragraph 3" },
  { key: "craftsi_intro", label: "Craftsi Page — Intro Text" },
  { key: "cta_heading", label: "CTA Band — Heading" },
  { key: "cta_text", label: "CTA Band — Text" },
  { key: "contact_heading", label: "Contact Section — Heading" },
  { key: "contact_subtext", label: "Contact Section — Text" },
  { key: "store_play_customer", label: "Store Link — Google Play (Customer App) · empty = Coming Soon" },
  { key: "store_play_vendor", label: "Store Link — Google Play (Vendor App) · empty = Coming Soon" },
  { key: "store_ios_customer", label: "Store Link — App Store (Customer App) · empty = Coming Soon" },
  { key: "store_ios_vendor", label: "Store Link — App Store (Vendor App) · empty = Coming Soon" },
];

const DigestSettings = () => {
  const [settings, setSettings] = useState(null);

  const load = () =>
    axios
      .get(`${API}/settings/digests`, { withCredentials: true })
      .then((r) => setSettings(r.data))
      .catch(() => setSettings({ weekly: true, chat: true }));

  useEffect(() => {
    load();
  }, []);

  const toggle = async (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    try {
      await axios.put(`${API}/settings/digests`, next, { withCredentials: true });
      toast.success(`Digest email turned ${next[key] ? "on" : "off"}`);
    } catch {
      toast.error("Could not update digest settings");
      load();
    }
  };

  if (!settings) return null;

  return (
    <div
      data-testid="digest-settings"
      className="mb-8 rounded-2xl border border-gray-100 bg-mist p-6"
    >
      <h3 className="font-heading text-base font-bold text-ink">Email digests</h3>
      <p className="mt-1 text-xs text-fog">Automatic summary emails to your official inbox.</p>
      <div className="mt-4 space-y-3">
        {[
          { key: "weekly", label: "Weekly digest — Mondays 9 AM (enquiries + applications)" },
          { key: "chat", label: "Daily chat summary — 8 AM (visitor questions to the assistant)" },
        ].map((d) => (
          <div key={d.key} className="flex items-center justify-between gap-4">
            <span className="text-sm text-ink">{d.label}</span>
            <button
              onClick={() => toggle(d.key)}
              data-testid={`digest-toggle-${d.key}`}
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-300 ${
                settings[d.key] ? "bg-navy" : "bg-gray-300"
              }`}
              aria-label={`Toggle ${d.key} digest`}
            >
              <span
                className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all duration-300 ${
                  settings[d.key] ? "left-6" : "left-1"
                }`}
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const SiteContentPanel = () => {
  const [content, setContent] = useState(null);
  const [history, setHistory] = useState([]);
  const [openHistory, setOpenHistory] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadContent = () =>
    axios.get(`${API}/content`).then((r) => setContent(r.data)).catch(() => setContent({}));

  const loadHistory = () =>
    axios
      .get(`${API}/content/history`, { withCredentials: true })
      .then((r) => setHistory(r.data))
      .catch(() => setHistory([]));

  useEffect(() => {
    loadContent();
    loadHistory();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/content`, { updates: content }, { withCredentials: true });
      toast.success("Site content updated — live on the website now");
      loadHistory();
    } catch {
      toast.error("Could not save the content");
    } finally {
      setBusy(false);
    }
  };

  const restore = async (key, value) => {
    try {
      await axios.put(`${API}/content`, { updates: { [key]: value } }, { withCredentials: true });
      toast.success("Previous version restored");
      setOpenHistory(null);
      loadContent();
      loadHistory();
    } catch {
      toast.error("Could not restore that version");
    }
  };

  if (content === null) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div data-testid="site-content-panel" className="mt-6 rounded-3xl border border-gray-100 bg-white p-8 shadow-sm">
      <DigestSettings />
      <h2 className="font-heading text-lg font-bold text-ink">Edit website text</h2>
      <p className="mt-1 text-sm text-fog">
        Changes go live immediately. Every save keeps the previous version so you can undo.
      </p>
      <div className="mt-6 space-y-5">
        {FIELDS.map((f) => {
          const versions = history.filter((h) => h.key === f.key);
          return (
            <div key={f.key}>
              <div className="mb-2 flex items-center justify-between">
                <label className="block text-xs font-semibold uppercase tracking-wider text-fog">
                  {f.label}
                </label>
                {versions.length > 0 && (
                  <button
                    onClick={() => setOpenHistory(openHistory === f.key ? null : f.key)}
                    data-testid={`history-${f.key}`}
                    className="flex items-center gap-1.5 text-xs font-semibold text-royal transition-colors duration-300 hover:text-navy"
                  >
                    <History className="h-3.5 w-3.5" /> History ({versions.length})
                  </button>
                )}
              </div>
              <textarea
                rows={2}
                value={content[f.key] || ""}
                onChange={(e) => setContent((c) => ({ ...c, [f.key]: e.target.value }))}
                data-testid={`content-${f.key}`}
                className="w-full resize-none rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink outline-none transition-all duration-300 focus:border-navy focus:ring-4 focus:ring-navy/10"
              />
              {openHistory === f.key && (
                <div className="mt-2 space-y-2 rounded-2xl border border-gray-100 bg-mist p-4">
                  {versions.map((v) => (
                    <div
                      key={v.id}
                      className="flex flex-wrap items-center gap-3 rounded-xl bg-white p-3"
                    >
                      <p className="flex-1 text-xs italic leading-relaxed text-fog">"{v.value}"</p>
                      <span className="text-[10px] text-fog">
                        {new Date(v.created_at).toLocaleString("en-IN", {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                      <button
                        onClick={() => restore(f.key, v.value)}
                        data-testid={`restore-${v.id}`}
                        className="flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-navy transition-all duration-300 hover:border-gold hover:text-gold-dark"
                      >
                        <RotateCcw className="h-3 w-3" /> Restore
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button
        onClick={save}
        disabled={busy}
        data-testid="content-save-button"
        className="mt-7 flex items-center gap-2 rounded-full bg-navy px-8 py-3 font-heading text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Save & Publish
      </button>
    </div>
  );
};

export default SiteContentPanel;
